import { SMUser } from "@/store";

const convertString = (s: string): string => {
  return s.charAt(0).toUpperCase() + s.slice(1).replaceAll("_", " ");
};

const convertStatus = (caseStatus: string, finalDecision: string, service: string) => {
  const status = caseStatus === "report_generated" ? "Complete" : convertString(caseStatus);

  if (service === "Eligibility Check") {
    if (finalDecision?.trim()) {
      return finalDecision;
    }
  }

  if (caseStatus === "complete" && finalDecision == "Approved") return "Granted";
  if (caseStatus === "complete" && finalDecision == "Rejected") return "Rejected";

  return status;
};

const getLabel = (a: string) => {
  if (!a) return "―";

  return a;
};

export type API_TCandidate_Home = {
  candidate_list: TCandidate_List[];
  has_candidates: boolean;
};

export type TCandidate_List = {
  candidate_name: string;
  case_id: number;
  case_status: string;
  final_decision: string;
  job_position: string;
  service: string;
  tasks_to_do: number;
  user_id: number;
};

export type TCandidate_Home = {
  case_id: number;
  candidate_name: string;
  job_position: string;
  service: string;
  status: string;
  tasks_to_do: number;
  user_id: number;
};

export const convertCandidateForTableHome = (a: TCandidate_List): TCandidate_Home => {
  return {
    case_id: a.case_id,
    candidate_name: a.candidate_name,
    job_position: getLabel(a.job_position),
    service: a.service,
    status: convertStatus(a.case_status, a.final_decision, a.service),
    tasks_to_do: a.tasks_to_do,
    user_id: a.user_id,
  };
};

export type API_TCandidates = {
  active: API_TCandidate[];
  archive: API_TCandidate[];
};

export type API_TCandidate = {
  candidate_name: string;
  case_id: number;
  case_status: string;
  final_decision: string;
  job_position: string;
  service: string;
  tasks_to_do: number;
  user_id: number;
  work_site: string;
  is_archived: boolean;
  has_child_case: boolean;
};

export type TCandidate = {
  case_id: number;
  name: string;
  position: string;
  service: string;
  status: string;
  tasks_to_do: number;
  user_id: number;
  work_site: string;
  is_archived: boolean;
  has_child_case: boolean;
  badgeType: string;
  inactive: boolean;
};

export const convertBadgeType = (status: string, inactive: boolean): string => {
  if (inactive) return "DeepTeal300";

  if (status === "Medium risk, eligible") return "VividOrange100";
  if (status === "High risk, not eligible") return "RaspberryPink400";
  if (status === "Low risk, eligible") return "DeepTeal500";
  if (status === "In Progress") return "DarkLimeGreen200";

  return "DarkLimeGreen200";
};

export const convertCandidateForTable = (a: API_TCandidate): TCandidate => {
  return {
    case_id: a.case_id,
    name: a.candidate_name,
    position: getLabel(a.job_position),
    service: a.service,
    tasks_to_do: a.tasks_to_do,
    user_id: a.user_id,
    work_site: a.work_site,
    is_archived: a.is_archived,
    has_child_case: a.has_child_case,
    status: convertStatus(a.case_status, a.final_decision, a.service),
    badgeType: convertBadgeType(
      convertStatus(a.case_status, a.final_decision, a.service),
      a.service === "Eligibility Check" ? false : a.case_status === "complete"
    ),
    inactive: a.service === "Eligibility Check" ? false : a.case_status === "complete",
  };
};

export type API_TCandidate_Summary = {
  candidate_name: string;
  date_of_birth: string;
  email: string;
  job_position: string;
  visa_expiration_date: string;
  work_site: string;
};

export type API_TCandidate_Case = {
  case_id: number;
  final_decision: string;
  owner_name: string;
  service: string;
  status: string;
  tasks_to_do: number;
  is_archived: boolean;
  has_child_case: boolean;
  start_date: string;
};

export type TCandidate_Case = {
  case_id: number;
  final_decision: string;
  owner_name: string;
  service: string;
  status: string;
  tasks_to_do: number;
  is_archived: boolean;
  has_child_case: boolean;
  badgeType: string;
  inactive: boolean;
  start_date: string;
};

export const convertCaseForTable = (a: API_TCandidate_Case): TCandidate_Case => {
  return {
    case_id: a.case_id,
    final_decision: a.final_decision || "―",
    owner_name: a.owner_name || "―",
    service: a.service || "―",
    tasks_to_do: a.tasks_to_do,
    is_archived: a.is_archived,
    has_child_case: a.has_child_case,
    status: convertStatus(a.status, a.final_decision, a.service),
    badgeType: convertBadgeType(
      convertStatus(a.status, a.final_decision, a.service),
      a.status === "complete"
    ),
    inactive: a.status === "complete",
    start_date: a.start_date,
  };
};

interface IButton {
  label: string;
  type: string;
  disabled?: boolean;
}

const BUTTON_MODEL: IButton = {
  label: "",
  type: "",
};

const CAN_START_VISA_SERVICE_STATUSES = ["Low risk, eligible", "Medium risk, eligible"];

export const buttonModel = (item: TCandidate_Case): IButton => {
  if (item.is_archived)
    return {
      label: "Unarchive",
      type: "",
    };

  if (item.has_child_case) return BUTTON_MODEL;

  if (item.tasks_to_do)
    return {
      label: item.tasks_to_do === 1 ? "1 task to do" : `${item.tasks_to_do} tasks to do`,
      type: "violet",
    };

  if (item.service === "Eligibility Check") {
    if (CAN_START_VISA_SERVICE_STATUSES.includes(item.status)) {
      return {
        label: "Start case",
        type: "",
        disabled: SMUser.isNotSponsor,
      };
    }

    if (item.status === "High risk, not eligible") {
      return {
        label: "Check report",
        type: "red",
      };
    }
  }

  return BUTTON_MODEL;
};
